// Cvanto AI — chat state machine for the web client. UI-only: every fact,
// limit and permission comes from the backend; this hook renders what the
// stream reports and keeps the interface honest under failure (limit reached,
// dropped connection, duplicate send, AI down -> ordinary search).
import { useCallback, useEffect, useRef, useState } from "react";
import {
  CvantoAIError,
  createConversation,
  deleteAllConversations,
  deleteConversation,
  getMessages,
  listConversations,
  ordinarySearch,
  renameConversation,
  sendMessage,
} from "./cvantoAIApi";

export const PHASE_LABELS = {
  understanding: "Розумію запит…",
  searching: "Шукаю серед товарів…",
  sending_request: "Надсилаю запит продавцям…",
};

const newClientMessageId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `c${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;

const localId = (prefix) => `${prefix}-${newClientMessageId()}`;

const titleFrom = (text) => String(text || "").replace(/\s+/g, " ").trim().slice(0, 80);

export default function useCvantoChat({
  conversationId: routeConversationId,
  onConversationCreated,
  onConversationMissing,
  // Unified Search (handoff §25.7): the caller's CURRENT manual search state
  // (query/city/price/...), read fresh on every turn via a ref so a parent
  // that keeps updating it (e.g. the user changed a filter) doesn't need to
  // recreate this hook. Only used the moment a brand-new conversation is
  // actually created (first message of a fresh chat) — an existing
  // conversation already has its own lastSearch and is never re-seeded.
  seedSearch,
} = {}) {
  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(true);
  const [conversationsError, setConversationsError] = useState("");
  const [conversationsCursor, setConversationsCursor] = useState(null);

  const [currentId, setCurrentId] = useState(routeConversationId || null);
  const [messages, setMessages] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historyCursor, setHistoryCursor] = useState(null);

  const [sending, setSending] = useState(false);
  const [usage, setUsage] = useState(null);

  const abortRef = useRef(null);
  const currentIdRef = useRef(currentId);
  const sendingRef = useRef(false);
  const lastRequestRef = useRef(null);
  const mountedRef = useRef(true);
  const seedSearchRef = useRef(seedSearch);

  currentIdRef.current = currentId;
  seedSearchRef.current = seedSearch;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  /* ------------------------------ conversations ------------------------------ */

  const loadConversations = useCallback(async ({ append = false, cursor = null } = {}) => {
    setConversationsLoading(true);
    setConversationsError("");

    try {
      const data = await listConversations({ cursor });
      if (!mountedRef.current) return;
      setConversations((prev) => (append ? [...prev, ...data.conversations] : data.conversations));
      setConversationsCursor(data.nextCursor || null);
    } catch (err) {
      if (!mountedRef.current) return;
      setConversationsError(err?.message || "Не вдалося завантажити розмови");
    } finally {
      if (mountedRef.current) setConversationsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  const loadMoreConversations = useCallback(() => {
    if (conversationsCursor && !conversationsLoading) loadConversations({ append: true, cursor: conversationsCursor });
  }, [conversationsCursor, conversationsLoading, loadConversations]);

  const touchConversation = useCallback((id, patch) => {
    setConversations((prev) => {
      const existing = prev.find((c) => c.id === id);
      const updated = { ...(existing || { id, title: "Нова розмова", messageCount: 0 }), ...patch };
      return [updated, ...prev.filter((c) => c.id !== id)];
    });
  }, []);

  /* --------------------------------- history --------------------------------- */

  const openConversation = useCallback(
    async (id) => {
      abortRef.current?.abort();
      sendingRef.current = false;
      setSending(false);
      setCurrentId(id);
      setMessages([]);
      setHistoryError("");
      setHistoryCursor(null);

      if (!id) return;

      setHistoryLoading(true);

      try {
        const data = await getMessages(id);
        if (!mountedRef.current || currentIdRef.current !== id) return;
        setMessages(data.messages || []);
        setHistoryCursor(data.nextCursor || null);
      } catch (err) {
        if (!mountedRef.current || currentIdRef.current !== id) return;
        if (err instanceof CvantoAIError && err.status === 404) {
          onConversationMissing?.(id);
          setCurrentId(null);
          return;
        }
        setHistoryError(err?.message || "Не вдалося завантажити розмову");
      } finally {
        if (mountedRef.current && currentIdRef.current === id) setHistoryLoading(false);
      }
    },
    [onConversationMissing]
  );

  // Follow the route (deep links, back/forward, switching in the list).
  useEffect(() => {
    if ((routeConversationId || null) !== currentIdRef.current) {
      openConversation(routeConversationId || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeConversationId]);

  useEffect(() => {
    if (routeConversationId) openConversation(routeConversationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadOlder = useCallback(async () => {
    const id = currentIdRef.current;
    if (!id || !historyCursor || historyLoading) return;

    setHistoryLoading(true);
    try {
      const data = await getMessages(id, { cursor: historyCursor });
      if (!mountedRef.current || currentIdRef.current !== id) return;
      setMessages((prev) => [...(data.messages || []), ...prev]);
      setHistoryCursor(data.nextCursor || null);
    } catch (err) {
      setHistoryError(err?.message || "Не вдалося завантажити історію");
    } finally {
      if (mountedRef.current) setHistoryLoading(false);
    }
  }, [historyCursor, historyLoading]);

  /* --------------------------------- sending --------------------------------- */

  const patchMessage = useCallback((id, patch) => {
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, ...(typeof patch === "function" ? patch(m) : patch) } : m)));
  }, []);

  const runTurn = useCallback(
    async ({ text, action, clientMessageId, userLocalId, assistantLocalId }) => {
      let cid = currentIdRef.current;

      try {
        if (!cid) {
          const created = await createConversation({ seedSearch: seedSearchRef.current || undefined });
          cid = created.conversation.id;
          currentIdRef.current = cid;
          setCurrentId(cid);
          touchConversation(cid, { ...created.conversation, title: titleFrom(text) || created.conversation.title });
          onConversationCreated?.(cid);
        }

        const controller = new AbortController();
        abortRef.current = controller;

        let assistantId = assistantLocalId;
        let done = null;

        await sendMessage(
          cid,
          { text: action ? undefined : text, action, clientMessageId },
          {
            signal: controller.signal,
            onEvent: (event, data) => {
              if (!mountedRef.current || currentIdRef.current !== cid) return;

              if (event === "start") {
                if (data.usage) setUsage(data.usage);
              } else if (event === "user_message") {
                patchMessage(userLocalId, { id: data.id, createdAt: data.createdAt, pending: false });
              } else if (event === "status") {
                patchMessage(assistantId, { phase: data.phase });
              } else if (event === "results") {
                patchMessage(assistantId, {
                  results: {
                    query: data.query,
                    total: data.total,
                    listings: data.listings || [],
                    people: data.people || [],
                    // Structured hints (Unified Search §25.5/§25.14) — lets a
                    // host (e.g. SearchResults' agent panel) sync its own
                    // filters/URL to what the agent actually searched with.
                    entityType: data.entityType || null,
                    city: data.city ?? null,
                    minPrice: data.minPrice ?? null,
                    maxPrice: data.maxPrice ?? null,
                    currency: data.currency ?? null,
                  },
                });
              } else if (event === "text") {
                patchMessage(assistantId, { text: data.text });
              } else if (event === "actions") {
                patchMessage(assistantId, { actions: data.actions || [] });
              } else if (event === "done") {
                done = data;
                patchMessage(assistantId, {
                  id: data.assistantMessage.id,
                  createdAt: data.assistantMessage.createdAt,
                  pending: false,
                  phase: null,
                  degraded: data.degraded,
                  fallback: data.fallback || null,
                  requestId: data.requestId || null,
                  status: data.status,
                });
                assistantId = data.assistantMessage.id;
                if (data.usage) setUsage(data.usage);
              } else if (event === "error") {
                patchMessage(assistantId, { pending: false, phase: null, error: { code: data.code, message: data.message } });
              }
            },
          }
        );

        if (!mountedRef.current || currentIdRef.current !== cid) return;

        touchConversation(cid, { lastMessageAt: Date.now(), updatedAt: Date.now() });

        // AI unavailable: still a marketplace — show ordinary Cvanto search.
        if (done?.fallback?.type === "ordinary_search" && done.fallback.q) {
          try {
            const items = await ordinarySearch(done.fallback.q);
            if (mountedRef.current && currentIdRef.current === cid) {
              patchMessage(assistantId, { results: { query: done.fallback.q, total: items.length, listings: items, people: [], ordinary: true } });
            }
          } catch {
            /* the fallback text already tells the user what happened */
          }
        }
      } catch (err) {
        if (err?.name === "AbortError" || !mountedRef.current) return;

        if (err instanceof CvantoAIError && err.code === "AI_LIMIT_REACHED") {
          // Keep what the user wrote; swap the thinking bubble for the notice.
          setUsage(err.data?.usage || null);
          setMessages((prev) => [
            ...prev.filter((m) => m.id !== assistantLocalId),
            { id: localId("limit"), role: "assistant", text: err.message, limit: { upgradePlan: err.data?.upgradePlan || null, usage: err.data?.usage || null }, createdAt: Date.now() },
          ]);
          patchMessage(userLocalId, { pending: false });
          return;
        }

        if (err instanceof CvantoAIError && err.code === "CONVERSATION_NOT_FOUND") {
          // Deleted meanwhile (another tab/device): the retry starts a new one.
          currentIdRef.current = null;
          setCurrentId(null);
          onConversationMissing?.(cid);
          patchMessage(assistantLocalId, { pending: false, phase: null, error: { code: err.code, message: "Цю розмову видалено. Спробуйте ще раз — я почну нову." } });
          return;
        }

        if (err instanceof CvantoAIError && err.code === "DUPLICATE_MESSAGE") {
          // The first attempt got through (e.g. the stream dropped after the
          // server accepted it): show what actually happened.
          if (currentIdRef.current) await openConversation(currentIdRef.current);
          return;
        }

        patchMessage(assistantLocalId, { pending: false, phase: null, error: { code: err?.code || "ERROR", message: err?.message || "Не вдалося отримати відповідь" } });
      } finally {
        sendingRef.current = false;
        if (mountedRef.current) setSending(false);
      }
    },
    [onConversationCreated, onConversationMissing, openConversation, patchMessage, touchConversation]
  );

  const submit = useCallback(
    ({ text, action, reuse } = {}) => {
      const clean = String(text || "").replace(/\s+/g, " ").trim();
      if (sendingRef.current) return false;
      if (!action && !clean) return false;

      sendingRef.current = true;
      setSending(true);

      const clientMessageId = reuse?.clientMessageId || newClientMessageId();
      const userLocalId = localId("user");
      const assistantLocalId = localId("assistant");
      const now = Date.now();
      const visibleText = action === "create_request" ? "Створити запит" : clean;

      lastRequestRef.current = { text: clean, action, clientMessageId };

      setMessages((prev) => [
        ...prev,
        { id: userLocalId, role: "user", text: visibleText, createdAt: now, pending: true },
        { id: assistantLocalId, role: "assistant", text: "", createdAt: now, pending: true, phase: "understanding" },
      ]);

      runTurn({ text: clean, action, clientMessageId, userLocalId, assistantLocalId });
      return true;
    },
    [runTurn]
  );

  const send = useCallback((text) => submit({ text }), [submit]);
  const sendAction = useCallback((action) => submit({ action }), [submit]);

  // Retry the last failed turn: drop the failed pair and resend (a fresh
  // client id, because the server released the previous one when it failed).
  const retry = useCallback(() => {
    const last = lastRequestRef.current;
    if (!last || sendingRef.current) return;
    setMessages((prev) => {
      const idx = prev.length - 1;
      return prev[idx]?.error ? prev.slice(0, Math.max(idx - 1, 0)) : prev;
    });
    submit({ text: last.text, action: last.action });
  }, [submit]);

  /* ------------------------------ management ------------------------------ */

  const startNewConversation = useCallback(() => {
    abortRef.current?.abort();
    sendingRef.current = false;
    setSending(false);
    setCurrentId(null);
    currentIdRef.current = null;
    setMessages([]);
    setHistoryError("");
    setHistoryCursor(null);
  }, []);

  const removeConversation = useCallback(
    async (id) => {
      await deleteConversation(id);
      setConversations((prev) => prev.filter((c) => c.id !== id));
      if (currentIdRef.current === id) startNewConversation();
    },
    [startNewConversation]
  );

  const removeAllConversations = useCallback(async () => {
    // Bounded per call on the server: repeat until it reports done.
    for (let i = 0; i < 20; i += 1) {
      const out = await deleteAllConversations();
      if (out.done) break;
    }
    setConversations([]);
    startNewConversation();
  }, [startNewConversation]);

  const rename = useCallback(async (id, title) => {
    const out = await renameConversation(id, title);
    setConversations((prev) => prev.map((c) => (c.id === id ? { ...c, title: out.title } : c)));
  }, []);

  return {
    conversations,
    conversationsLoading,
    conversationsError,
    hasMoreConversations: !!conversationsCursor,
    loadConversations,
    loadMoreConversations,

    currentId,
    messages,
    historyLoading,
    historyError,
    hasMoreHistory: !!historyCursor,
    loadOlder,
    openConversation,
    reloadHistory: () => currentIdRef.current && openConversation(currentIdRef.current),

    sending,
    usage,
    send,
    sendAction,
    retry,

    startNewConversation,
    removeConversation,
    removeAllConversations,
    rename,
  };
}
